import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Globe2, Languages, BookOpen, FileSearch, Plus, Trash2, Edit3,
  Eye, EyeOff, ExternalLink, Save, X, ChevronLeft, BarChart3, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_OPTIONS = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "ta", label: "Tamil" },
];

const DEFAULT_FORM = {
  title: "",
  slug: "",
  excerpt: "",
  body_markdown: "",
  category: "Guides",
  cover_image_url: "",
  tags: "",
  read_minutes: 4,
  language: "en",
  published: true,
  seo_title: "",
  seo_description: "",
};

export default function SEODashboardPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Admin guard
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const fetchAll = async () => {
    setLoadingData(true);
    try {
      const [m, p] = await Promise.all([
        axios.get(`${API}/blog/admin/seo-metrics`, { withCredentials: true }),
        axios.get(`${API}/blog/admin/list`, { withCredentials: true }),
      ]);
      setMetrics(m.data);
      setPosts(p.data?.posts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load SEO data");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchAll();
  }, [user]);

  const openCreate = () => {
    setEditingSlug(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingSlug(post.slug);
    setForm({
      title: post.title || "",
      slug: post.slug || "",
      excerpt: post.excerpt || "",
      body_markdown: post.body_markdown || "",
      category: post.category || "Guides",
      cover_image_url: post.cover_image_url || "",
      tags: (post.tags || []).join(", "),
      read_minutes: post.read_minutes || 4,
      language: post.language || "en",
      published: !!post.published,
      seo_title: post.seo_title || "",
      seo_description: post.seo_description || "",
    });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.excerpt.trim() || !form.body_markdown.trim()) {
      toast.error("Title, excerpt, and body are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        excerpt: form.excerpt.trim(),
        body_markdown: form.body_markdown,
        category: form.category.trim() || "Guides",
        cover_image_url: form.cover_image_url.trim() || null,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        read_minutes: parseInt(form.read_minutes) || 4,
        language: form.language || "en",
        published: !!form.published,
        seo_title: form.seo_title.trim() || undefined,
        seo_description: form.seo_description.trim() || undefined,
      };
      if (editingSlug) {
        await axios.put(`${API}/blog/admin/update/${editingSlug}`, payload, { withCredentials: true });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog/admin/create`, payload, { withCredentials: true });
        toast.success("Post published");
      }
      setShowForm(false);
      setEditingSlug(null);
      setForm(DEFAULT_FORM);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/blog/admin/update/${post.slug}`, { published: !post.published }, { withCredentials: true });
      toast.success(post.published ? "Unpublished" : "Published");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/admin/delete/${post.slug}`, { withCredentials: true });
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  if (loading || (user && user.role !== "admin")) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8" data-testid="seo-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-1.5">
              <ChevronLeft className="w-3.5 h-3.5" /> Admin
            </Link>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 flex items-center gap-2" data-testid="seo-dashboard-title">
              <BarChart3 className="w-7 h-7 text-blue-600" /> SEO Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">Track organic SEO surface area and publish blog posts to rank for new keywords.</p>
          </div>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-blog-post-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Blog Post
          </Button>
        </div>

        {/* Metrics */}
        {loadingData ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
          </div>
        ) : metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="seo-metrics-grid">
            <MetricCard
              icon={<FileSearch className="w-5 h-5 text-blue-600" />}
              label="SEO Landing Pages"
              value={metrics.static.seo_landing_pages}
              hint="Programmatic landing pages"
              testid="metric-seo-landing"
            />
            <MetricCard
              icon={<Globe2 className="w-5 h-5 text-emerald-600" />}
              label="Profession Pages"
              value={metrics.static.profession_pages}
              hint="/invoice-format/:slug"
              testid="metric-profession"
            />
            <MetricCard
              icon={<BookOpen className="w-5 h-5 text-violet-600" />}
              label="Blog Articles"
              value={metrics.static.static_blog_articles + metrics.blog.published}
              hint={`${metrics.static.static_blog_articles} static + ${metrics.blog.published} dynamic`}
              testid="metric-blog-total"
            />
            <MetricCard
              icon={<Languages className="w-5 h-5 text-amber-600" />}
              label="Languages"
              value={metrics.static.languages_supported}
              hint="EN · HI · MR · GU · TA"
              testid="metric-languages"
            />
          </div>
        )}

        {/* Top Viewed + Sitemap links */}
        {metrics && (
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="md:col-span-2 bg-white rounded-2xl border border-slate-100 p-5" data-testid="top-viewed-posts">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <h2 className="font-bold text-slate-900">Top viewed (dynamic)</h2>
              </div>
              {metrics.blog.top_viewed.length === 0 ? (
                <p className="text-xs text-slate-400 py-4">No view data yet — publish a post to start tracking.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {metrics.blog.top_viewed.map((p) => (
                    <div key={p.slug} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{p.title}</p>
                        <p className="text-[11px] text-slate-400">/{p.slug} · {p.language}</p>
                      </div>
                      <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded-md">{p.views || 0} views</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="seo-shortcuts">
              <h2 className="font-bold text-slate-900 mb-3">Quick links</h2>
              <ul className="space-y-2 text-sm">
                <li><a href="/sitemap.xml" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> sitemap.xml</a></li>
                <li><a href="/robots.txt" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> robots.txt</a></li>
                <li><Link to="/blog" className="inline-flex items-center gap-1.5 text-slate-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> Public blog</Link></li>
                <li><Link to="/for-ca" className="inline-flex items-center gap-1.5 text-slate-600 hover:underline"><ExternalLink className="w-3.5 h-3.5" /> CA landing</Link></li>
              </ul>
            </div>
          </div>
        )}

        {/* Language coverage */}
        {metrics && metrics.blog.languages.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-8" data-testid="lang-coverage">
            <h2 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Languages className="w-4 h-4 text-amber-600" /> Language coverage (dynamic posts)</h2>
            <div className="flex flex-wrap gap-2">
              {metrics.blog.languages.map((b) => (
                <span key={b.language} className="text-xs font-semibold bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full">
                  {LANG_OPTIONS.find(l => l.code === b.language)?.label || b.language} · {b.count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Posts table */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="dynamic-posts-table">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900">Dynamic blog posts ({posts.length})</h2>
          </div>
          {posts.length === 0 ? (
            <div className="px-5 py-12 text-center" data-testid="no-posts-state">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No posts yet. Publish your first article to start ranking on long-tail GST keywords.</p>
              <Button onClick={openCreate} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" data-testid="empty-create-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Create first post
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {posts.map((p) => (
                <div key={p.slug} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`post-row-${p.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 truncate">{p.title}</p>
                      {p.published ? (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">Live</span>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Draft</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">/blog/{p.slug} · {p.language} · {p.category} · {p.views || 0} views</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Link to={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 hover:bg-slate-100 rounded-lg" title="Open public page">
                      <ExternalLink className="w-4 h-4 text-slate-500" />
                    </Link>
                    <button onClick={() => togglePublish(p)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`toggle-publish-${p.slug}`} title={p.published ? "Unpublish" : "Publish"}>
                      {p.published ? <EyeOff className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-slate-500" />}
                    </button>
                    <button onClick={() => openEdit(p)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`edit-post-${p.slug}`} title="Edit">
                      <Edit3 className="w-4 h-4 text-slate-500" />
                    </button>
                    <button onClick={() => deletePost(p)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`delete-post-${p.slug}`} title="Delete">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="post-form-modal">
          <form onSubmit={submitForm} className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">{editingSlug ? "Edit blog post" : "New blog post"}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="p-1.5 hover:bg-slate-100 rounded-lg">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Title *">
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  data-testid="form-title"
                  required
                  maxLength={160}
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Slug (auto-generated if blank)">
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500"
                    placeholder="my-awesome-post"
                    data-testid="form-slug"
                  />
                </Field>
                <Field label="Category">
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    data-testid="form-category"
                  />
                </Field>
              </div>
              <Field label="Excerpt * (10–300 chars)">
                <textarea
                  rows={2}
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  data-testid="form-excerpt"
                  required
                  minLength={10}
                  maxLength={300}
                />
              </Field>
              <Field label="Body (Markdown supported: #, ##, -, *)">
                <textarea
                  rows={12}
                  value={form.body_markdown}
                  onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500"
                  data-testid="form-body"
                  required
                  minLength={20}
                />
              </Field>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Language">
                  <select
                    value={form.language}
                    onChange={(e) => setForm({ ...form, language: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    data-testid="form-language"
                  >
                    {LANG_OPTIONS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                  </select>
                </Field>
                <Field label="Read minutes">
                  <input
                    type="number"
                    value={form.read_minutes}
                    onChange={(e) => setForm({ ...form, read_minutes: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    min={1}
                    max={60}
                    data-testid="form-read-minutes"
                  />
                </Field>
                <Field label="Tags (comma-separated)">
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                    placeholder="gst, invoice, freelance"
                    data-testid="form-tags"
                  />
                </Field>
              </div>
              <Field label="Cover image URL (optional)">
                <input
                  type="url"
                  value={form.cover_image_url}
                  onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  placeholder="https://..."
                  data-testid="form-cover"
                />
              </Field>
              <Field label="SEO title (defaults to title)">
                <input
                  type="text"
                  value={form.seo_title}
                  onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  data-testid="form-seo-title"
                />
              </Field>
              <Field label="SEO description (defaults to excerpt)">
                <textarea
                  rows={2}
                  value={form.seo_description}
                  onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  data-testid="form-seo-description"
                />
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  data-testid="form-published"
                  className="rounded"
                />
                Publish immediately (uncheck to save as draft)
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="form-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                {editingSlug ? "Update post" : "Publish post"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, hint, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-3xl font-black text-slate-900">{value}</p>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
